// Per-browser identity for visitors who have not made an account.
//
// A student who plays for an hour as a guest and comes back tomorrow should
// find their XP, streak and quests exactly where they left them. That works
// because this browser mints one random id, keeps it, and the server stores a
// copy of the guest's progress under it.
//
// This is deliberately NOT a fingerprint. Canvas/font/audio fingerprinting is
// unreliable (it drifts with every browser update) and, more to the point, it
// is something a child cannot opt out of. A stored id keeps the promise the
// sign-up prompt actually makes: clear your browser data and the progress is
// gone — unless you made an account.
//
// The id lives in localStorage AND in a cookie because browsers clear the two
// stores independently: Chrome's "Cookies and other site data" wipes both, but
// a private window, an extension, or iOS's 7-day script-storage eviction can
// take one and leave the other. Whichever survives re-seeds the other on the
// next visit, so ordinary browsing never silently loses a guest's work.

const LS_KEY = 'sv_device_id';
const COOKIE = 'sv_did';
const TWO_YEARS = 60 * 60 * 24 * 730;

// Same shape the server validates, so a hand-edited value is rejected in one
// place rather than becoming a weird primary key in SQLite.
const VALID = /^[A-Za-z0-9_-]{8,64}$/;

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|;\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

function writeCookie(name, value) {
  try {
    // Lax, not None: the id is only ever read by our own pages. Secure only on
    // https, otherwise the local dev server (http://localhost:8080) silently
    // drops the cookie and every dev reload looks like a brand-new device.
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${TWO_YEARS}; Path=/; SameSite=Lax${secure}`;
  } catch { /* cookies disabled — localStorage still carries the id */ }
}

function mint() {
  try {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID().replace(/-/g, '');
    }
  } catch { /* fall through */ }
  try {
    const b = new Uint8Array(16);
    window.crypto.getRandomValues(b);
    return Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  } catch { /* fall through */ }
  // Last resort for very old browsers. Weaker, but a guest id only has to be
  // unique, not unguessable-in-the-cryptographic-sense: it carries no account.
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 12)}`;
}

let cached = null;

/**
 * The id for this browser. Stable across visits, minted on first call.
 * Always re-writes both stores: if one was cleared, the survivor heals it.
 */
export function getDeviceId() {
  if (cached) return cached;

  let id = null;
  try { id = localStorage.getItem(LS_KEY); } catch { /* private mode */ }
  if (!VALID.test(id || '')) id = readCookie(COOKIE);
  if (!VALID.test(id || '')) id = mint();

  cached = id;
  try { localStorage.setItem(LS_KEY, id); } catch { /* cookie-only device */ }
  writeCookie(COOKIE, id);
  return id;
}

/**
 * A human-readable "Chrome on Windows" for the guest banner and for the
 * device row in SQLite. Coarse on purpose — enough to tell a student which
 * browser holds their progress, not enough to be a tracking signal.
 */
export function deviceLabel() {
  const ua = (window.navigator && window.navigator.userAgent) || '';
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\/|Opera/.test(ua) ? 'Opera' :
    /SamsungBrowser/.test(ua) ? 'Samsung Internet' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Safari\//.test(ua) ? 'Safari' : 'this browser';
  const os =
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad|iPod/.test(ua) ? 'iPhone/iPad' :
    /Windows/.test(ua) ? 'Windows' :
    /Mac OS X/.test(ua) ? 'Mac' :
    /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

/** Drop this browser's identity (used when a guest asks to start over). */
export function forgetDevice() {
  cached = null;
  try { localStorage.removeItem(LS_KEY); } catch { /* nothing to do */ }
  try {
    document.cookie = `${COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch { /* nothing to do */ }
}
